// Attributely Tracking Script v2
// Version: 2.0.0 (Complete rewrite with journey tracking and raw UTM storage)

declare global {
  // Extend the Window interface to include AttributelyData, journey tracking, __attrEarlyQueue, and __jotformEarlyQueue
  interface Window {
    AttributelyData: AttributionDataV2 | null;
    AttributelyJourney?: JourneyEntry[];
    AttributelyFormSubmit?: (formData: Record<string, any>, formId?: string) => void;
    attributely?: any;
    __attrEarlyQueue?: Record<string, any>[];
    __jotformEarlyQueue?: Record<string, any>[];
    __iframeFormEarlyQueue?: Record<string, any>[];
    // Chat widget globals
    Intercom?: any;
    HubSpotConversations?: any;
    Tawk_API?: any;
    LC_API?: any;
    tidioChatApi?: any;
    zE?: any;
  }
}

// Declare Calendly as a global variable since its script is loaded externally
declare var Calendly: any;

// Ensures this file is treated as a module
export {};

interface JourneyEntry {
  timestamp: string;
  channel: string;
  source: string;
  medium: string;
  campaign: string;
  term: string;
  content: string;
  landingPage: string;
  referrerURL: string;
  gclid?: string;
  msclkid?: string;
  fbclid?: string;
  li_fat_id?: string;
  gbraid?: string;
  wbraid?: string;
  pageViews: PageView[];
}

interface PageView {
  url: string;
  timestamp: string;
}

interface AttributionDataV2 {
  // Channel identification
  channel: string;
  
  // Raw UTM parameters
  attribution_source: string;
  attribution_medium: string;
  attribution_campaign: string;
  attribution_term: string;
  attribution_content: string;
  
  // Page information
  landingPage: string;
  lastLandingPage: string;
  referrerURL: string;
  lastReferrerURL: string;
  lastViewedURL: string;
  landingpagefolder: string;
  
  // Timestamps
  firstSeen: string;
  lastSeen: string;
  
  // Custom fields
  customFields: Record<string, string>;
  
  // Click IDs
  gclid?: string;
  msclkid?: string;
  fbclid?: string;
  gbraid?: string;
  wbraid?: string;
  
  // Website ID
  websiteId?: string;
  
  // First touch data (for attribution)
  first_channel?: string;
  first_source?: string;
  first_medium?: string;
  first_campaign?: string;
  first_term?: string;
  first_content?: string;
  first_landingpage?: string;
  first_landingpagefolder?: string;
  
  // Journey tracking
  journeyId?: string;
  visitCount?: number;
  
  // Contact information
  email?: string;
  
  // Form field data
  formFields?: Record<string, string>;
}

// --- Provider handler helpers (top-level) ---
function flattenTallyFields(tallyPayload: any): Record<string, any> {
  const result: Record<string, any> = {};
  if (Array.isArray(tallyPayload.fields)) {
    tallyPayload.fields.forEach(field => {
      if (field.type !== 'HIDDEN_FIELDS' && field.answer && field.title) {
        result[field.title] = field.answer.value;
      }
    });
  }
  return result;
}
function extractTallyFormId(tallyPayload: any): string | undefined {
  return tallyPayload.formId || undefined;
}
function flattenJotFormFields(jotFormData: any): Record<string, any> {
  if (jotFormData && typeof jotFormData === 'object' && !Array.isArray(jotFormData)) {
    return jotFormData;
  }
  return {};
}
function flattenPaperformFields(paperformData: any[]): Record<string, any> {
  const result: Record<string, any> = {};
  if (Array.isArray(paperformData)) {
    paperformData.forEach((field) => {
      if (field && field.label && field.value !== undefined) {
        result[field.label] = field.value;
      }
    });
  }
  return result;
}
function isMessageEvent(event: any): event is MessageEvent {
  return typeof event === 'object' && event !== null && 'origin' in event && 'data' in event;
}

// --- Utility: Clean URL (strip query/hash) ---
function cleanUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.origin + u.pathname;
  } catch {
    return url;
  }
}

// --- Provider Handlers (top-level, single instance) ---
const providerHandlers: Record<string, (event: MessageEvent | CustomEvent, parsedData?: any) => { fields: Record<string, any>, formId?: string, submissionId?: string, type?: string } | null> = {
  // Tally
  'Tally.FormSubmitted': (event, parsedData) => {
    if (isMessageEvent(event) && event.origin !== "https://tally.so") {
      console.warn('[Attributely v2][Tally] Ignored message: wrong origin', event.origin);
      return null;
    }
    if (parsedData && parsedData.payload) {
      const flatFields = flattenTallyFields(parsedData.payload);
      // Clean landing page fields
      ['landingPage', 'first_landingpage', 'lastLandingPage'].forEach(key => {
        if (flatFields[key]) flatFields[key] = cleanUrl(flatFields[key]);
      });
      const formId = extractTallyFormId(parsedData.payload);
      console.log('[Attributely v2][Tally] Flattened fields:', flatFields, 'formId:', formId);
      return { fields: flatFields, formId };
    }
    console.warn('[Attributely v2][Tally] No payload found in parsed data:', parsedData);
    return null;
  },
  // JotForm
  'submission-completed': (event) => {
    if (isMessageEvent(event)) {
      if (event.origin && !event.origin.includes('jotform.com')) return null;
      if (event.data && event.data.submissionData) {
        const flatFields = flattenJotFormFields(event.data.submissionData);
        // Clean landing page fields
        ['landingPage', 'first_landingpage', 'lastLandingPage'].forEach(key => {
          if (flatFields[key]) flatFields[key] = cleanUrl(flatFields[key]);
        });
        const formId = event.data.formID || event.data.formId || undefined;
        return { fields: flatFields, formId };
      }
    }
    return null;
  },
  // Paperform
  'PaperformSubmission': (event) => {
    const customEvent = event as CustomEvent;
    if (!customEvent.detail) return null;
    const detail = customEvent.detail;
    if (!detail.form_id || !detail.data) return null;
    const flatFields = flattenPaperformFields(detail.data);
    // Clean landing page fields
    ['landingPage', 'first_landingpage', 'lastLandingPage'].forEach(key => {
      if (flatFields[key]) flatFields[key] = cleanUrl(flatFields[key]);
    });
    const formId = detail.form_id;
    const submissionId = detail.submission_id;
    console.log('[Attributely v2][Paperform] Flattened fields:', flatFields, 'formId:', formId, 'submissionId:', submissionId);
    return { fields: flatFields, formId, submissionId };
  },
  // GoHighLevel
  'GoHighLevel.FormSubmitted': (event: MessageEvent | CustomEvent) => {
    if (!isMessageEvent(event)) return null;
    if (event.origin !== "https://api.leadconnectorhq.com") {
      console.warn('[Attributely v2][GoHighLevel] Ignored message: wrong origin', event.origin);
      return null;
    }
    if (Array.isArray(event.data) && typeof event.data[2] === 'string') {
      try {
        const fields = JSON.parse(event.data[2]);
        // Clean landing page fields
        ['landingPage', 'first_landingpage', 'lastLandingPage'].forEach(key => {
          if (fields[key]) fields[key] = cleanUrl(fields[key]);
        });
        const formId = event.data[3] || undefined;
        const submissionId = event.data[4] || undefined;
        console.log('[Attributely v2][GoHighLevel] Parsed fields:', fields, 'formId:', formId, 'submissionId:', submissionId);
        return { fields, formId, submissionId };
      } catch (err) {
        console.error('[Attributely v2][GoHighLevel] Failed to parse form data:', event.data[2], err);
        return null;
      }
    }
    return null;
  },
  // Add Cal.com handler to providerHandlers
  'CalCom.MeetingBooked': (event: MessageEvent | CustomEvent) => {
    if (!isMessageEvent(event) || !event.origin.includes('cal.com')) return null;
    const { type, data } = event.data || {};
    if (type !== 'bookingSuccessful' || !data || !data.booking) return null;
    const booking = data.booking;
    // Prefer responses, fallback to attendees
    const attendee = (booking.responses && booking.responses.email) ? booking.responses : (booking.attendees && booking.attendees[0]) || {};
    const scheduled_date: string | undefined = booking.startTime || data.date || null;
    // Flatten custom fields/questions
    const customFields = booking.responses || booking.customInputs || {};
    // Normalize fields, EXCLUDE location and meeting_title
    const { location, title, eventType, ...restBooking } = booking;
    const fields: Record<string, any> = {
      name: attendee.name,
      email: attendee.email,
      scheduled_date, // always use this key for UI
      video_call_url: booking.videoCallUrl,
      ...customFields
    };
    console.log('[Attributely v2][Cal.com] Normalized meeting fields (no location, no meeting_title):', fields);
    return { fields, formId: booking.id || undefined, submissionId: booking.id || undefined, type: 'Meeting' };
  },
  // Add HubSpot meeting handler to providerHandlers
  'HubSpot.MeetingBooked': (event: MessageEvent | CustomEvent) => {
    const bookingResponse = (event as any).data?.meetingsPayload?.bookingResponse;
    if (!bookingResponse) return null;

    // Extract contact info from postResponse
    const contact = bookingResponse.postResponse?.contact || {};
    const name = contact.name || [contact.firstName, contact.lastName].filter(Boolean).join(' ');
    const email = contact.email;

    // Extract scheduled date
    let scheduled_date: string | undefined = undefined;
    if (bookingResponse.event?.dateTime) {
      scheduled_date = new Date(bookingResponse.event.dateTime).toISOString();
    } else if (bookingResponse.event?.dateString) {
      scheduled_date = bookingResponse.event.dateString;
    }

    // Custom fields from formFields (array of {name, value})
    let customFields = {};
    if (Array.isArray(bookingResponse.event?.formFields)) {
      bookingResponse.event.formFields.forEach((f: any) => {
        if (f.name) customFields[f.name] = f.value;
      });
    }

    const fields: Record<string, any> = {
      name,
      email,
      scheduled_date,
      formGuid: bookingResponse.formGuid,
      ...customFields
    };

    console.log('[Attributely v2][HubSpot] Normalized meeting fields:', fields);
    return { fields, formId: bookingResponse.formGuid, submissionId: undefined, type: 'Meeting' };
  },
  // Add more providers here as needed
  // Tidycal
  'Tidycal.MeetingBooked': (event: MessageEvent | CustomEvent) => {
    // Accept both direct event and parsedData
    let booking = null;
    if (isMessageEvent(event) && event.data && event.data.data && event.data.event === 'bookingComplete') {
      booking = event.data.data.booking as any;
    } else if (event && (event as any).booking) {
      booking = (event as any).booking as any;
    }
    if (!booking) return null;
    const contact = (booking as any).contact || {};
    const fields: Record<string, any> = {
      name: contact.name,
      email: contact.email,
      scheduled_date: (booking as any).starts_at,
      location: (booking as any).location,
      booking_id: (booking as any).id,
      contact_id: (booking as any).contact_id,
    };
    console.log('[Attributely v2][Tidycal] Normalized meeting fields:', fields);
    return { fields, formId: booking.id || undefined, submissionId: booking.id || undefined, type: 'Meeting' };
  },
  // Cognito Forms
  'CognitoForm.AfterSubmit': (event: any) => {
    const entry = event?.data?.entry;
    if (!entry) return null;
    const fields: Record<string, any> = {};
    for (const [key, value] of Object.entries(entry)) {
      // Exclude irrelevant fields
      if (key === 'Entry' || key === '$type' || key === '$version') continue;
      // Exclude hidden fields by name
      if (typeof key === 'string' && key.toLowerCase().includes('hidden')) continue;
      // Flatten Name field if it's an object
      if (key === 'Name' && value && typeof value === 'object') {
        const nameParts = [
          (value as any).First,
          (value as any).MiddleInitial,
          (value as any).Last
        ].filter(Boolean);
        if (nameParts.length) {
          fields['Name'] = nameParts.join(' ');
        }
        continue;
      }
      // Exclude objects with only null/empty values
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const hasMeaningful = Object.values(value).some(v => v !== null && v !== undefined && v !== '');
        if (!hasMeaningful) continue;
      }
      // Only include primitives or meaningful objects
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        fields[key] = value;
      } else if (value && typeof value === 'object' && !Array.isArray(value)) {
        // For other objects, JSON.stringify if meaningful
        fields[key] = JSON.stringify(value);
      }
    }
    // Clean landing page fields
    ['landingPage', 'first_landingpage', 'lastLandingPage'].forEach(key => {
      if (fields[key]) fields[key] = cleanUrl(fields[key]);
    });
    const formId = event?.data?.form?.Id || undefined;
    console.log('[Attributely v2][Cognito] Normalized form fields:', fields);
    return { fields, formId };
  },
  // Calendly
  'calendly.event_scheduled': (event, parsedData) => {
    // Calendly exposes event URI in postMessage
    let eventUri = undefined;
    // Type guard for MessageEvent
    const isMsgEvent = (e: any): e is MessageEvent => typeof e === 'object' && e !== null && 'data' in e;
    if (parsedData && parsedData.payload && parsedData.payload.event && parsedData.payload.event.uri) {
      eventUri = parsedData.payload.event.uri;
    } else if (isMsgEvent(event) && event.data && event.data.payload && event.data.payload.event && event.data.payload.event.uri) {
      eventUri = event.data.payload.event.uri;
    }
    if (!eventUri) {
      console.warn('[Attributely v2][Calendly] No event URI found in postMessage:', event);
      return null;
    }
    // Use the full URI as submissionId
    const submissionId = eventUri;
    // No email or form fields available at this stage
    const fields = {};
    console.log('[Attributely v2][Calendly] Normalized event:', { submissionId, fields });
    return { fields, submissionId, type: 'Meeting' };
  },
  // Typeform
  'form-submit': (event) => {
    // Typeform postMessage
    const data = (event && (event as any).data) || {};
    const submissionId = data.responseId || data.response_id;
    const formId = data.formId;
    if (!submissionId) {
      console.warn('[Attributely v2][Typeform] No responseId found in form-submit message:', event);
      return null;
    }
    const fields = {}; // No fields in postMessage
    console.log('[Attributely v2][Typeform] Normalized event:', { submissionId, formId, fields });
    return { fields, formId, submissionId, type: 'Web Form' };
  },
};

// Make providerHandlers globally accessible
(window as any).providerHandlers = providerHandlers;
console.log('[Attributely v2][Init] providerHandlers set on window', Object.keys(providerHandlers));

// --- PaperformSubmission event listener (top-level, single instance) ---
window.addEventListener('PaperformSubmission', (event: any) => {
  console.log('[Attributely v2][Paperform] PaperformSubmission event received:', event);
  if (!('detail' in event)) {
    console.warn('[Attributely v2][Paperform] Event does not have detail property:', event);
    return;
  }
  const customEvent = event as CustomEvent;
  // Use global providerHandlers
  const handler = (window as any).providerHandlers['PaperformSubmission'];
  if (!handler) {
    console.warn('[Attributely v2][Paperform] No handler found for PaperformSubmission');
    return;
  }
  const normalized = handler(customEvent);
  console.log('[Attributely v2][Paperform] Normalized data:', normalized);
  if (!normalized) {
    console.warn('[Attributely v2][Paperform] Handler did not return normalized data.');
    return;
  }
  if (typeof window.AttributelyFormSubmit === 'function') {
    console.log('[Attributely v2][Paperform] Calling AttributelyFormSubmit with:', normalized.fields, normalized.formId);
    window.AttributelyFormSubmit(normalized.fields, normalized.formId);
  } else {
    if (!window.__iframeFormEarlyQueue) window.__iframeFormEarlyQueue = [];
    window.__iframeFormEarlyQueue.push({ fields: normalized.fields, formId: normalized.formId });
    console.log('[Attributely v2][Paperform] AttributelyFormSubmit not ready, queued normalized data.');
  }
});

// Listen for HubSpot form submissions
if (typeof window !== 'undefined') {
  document.addEventListener('hsFormCallback', function(event: any) {
    console.log('[Attributely v2][HubSpot] hsFormCallback event received:', event);
    if (event.detail && event.detail.eventName === 'onFormSubmit') {
      if (typeof window.AttributelyFormSubmit === 'function') {
        console.log('[Attributely v2][HubSpot] Calling AttributelyFormSubmit with:', event.detail.data);
        window.AttributelyFormSubmit(event.detail.data);
      } else {
        console.warn('[Attributely v2][HubSpot] AttributelyFormSubmit not ready, event ignored.');
      }
    }
  });

  // Expose a global function for custom JS forms
  window.AttributelyFormSubmit = function(formData: Record<string, any>, formId?: string) {
    console.log('[Attributely v2][Global] AttributelyFormSubmit called with:', formData, formId);
    let instance = null;
    if (
      window.attributely &&
      typeof window.attributely === 'object' &&
      'instance' in window.attributely &&
      window.attributely.instance &&
      typeof window.attributely.instance.handleExternalFormSubmit === 'function'
    ) {
      instance = window.attributely.instance;
    } else if (
      (window as any).attributelyInstance &&
      typeof (window as any).attributelyInstance.handleExternalFormSubmit === 'function'
    ) {
      instance = (window as any).attributelyInstance;
    }
    if (instance) {
      instance.handleExternalFormSubmit(formData, formId);
    } else {
      console.warn('[Attributely v2][Global] No Attributely instance available for handleExternalFormSubmit');
    }
  };
  console.log('[Attributely v2][Init] window.AttributelyFormSubmit defined');
}

class AttributelyV2 {
  protected websiteId: string;
  private storageKey: string = '_attribution_data_v2';
  private journeyKey: string = '_attribution_journey';
  private cookieExpiryDays: number = 180;
  protected attributionData: AttributionDataV2 | null = null;
  protected journey: JourneyEntry[] = [];
  protected apiUrl = 'https://app.attributely.ai';
  protected anonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVqaHJ4eHV5ZGZ2bGtkb2t3cmlkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDE1NDM5MTIsImV4cCI6MjA1NzExOTkxMn0.oDI5daI_M6tR39hsQ9ogP96NYWFPhKchh1kHroxjqk8';
  protected version: string = '2.0.0';
  private excludedUrls: string[] = [];

  private standardizedChannels = {
    DIRECT: 'Direct',
    EMAIL: 'Email',
    PAID_SEARCH: 'Paid Search',
    PAID_SOCIAL: 'Paid Social',
    OTHER_CAMPAIGNS: 'Other Campaigns',
    ORGANIC_SEARCH: 'Organic Search',
    ORGANIC_SOCIAL: 'Organic Social',
    REFERRAL: 'Referral',
    AI_SEARCH: 'AI Search',
  };

  private aiSearchDomains = [
    'perplexity.ai',
    'you.com',
    'claude.ai',
    'chat.openai.com',
    'copilot.microsoft.com',
    'gemini.google.com',
    'search.brave.com',
    'chatgpt.com',
    'phind.com',
    'andisearch.com',
    'arc.net',
    'pi.ai',
    'gptgo.ai',
    'writesonic.com',
    'neeva.com',
    'ai.com',
    'searchgpt.com',
    'search.ai',
    'yep.com'
  ];

  private searchEngines = [
    { domain: 'google', name: 'Google', queryParam: 'q' },
    { domain: 'bing', name: 'Bing', queryParam: 'q' },
    { domain: 'yahoo', name: 'Yahoo', queryParam: 'p' },
    { domain: 'duckduckgo', name: 'DuckDuckGo', queryParam: 'q' },
    { domain: 'baidu', name: 'Baidu', queryParam: 'wd' },
    { domain: 'yandex', name: 'Yandex', queryParam: 'text' }
  ];

  private socialNetworks = [
    'facebook.com',
    'instagram.com',
    'linkedin.com',
    'twitter.com',
    'x.com',
    'pinterest.com',
    'reddit.com',
    'youtube.com',
    't.me',
    'tiktok.com'
  ];

  constructor(websiteId: string) {
    console.log(`Attributely v2: Initializing with website ID: ${websiteId}`);
    this.websiteId = websiteId;
    this.verifyScript().then(() => {
      this.initStorage();
      this.setupPageViewTracking();
      this.init();
      this.observeForms();
      this.trackPageView(); // Still track page views, just without time on page
    }).catch(error => {
      console.error('Attributely v2: Script verification failed:', error);
    });
  }

  private async verifyScript(): Promise<void> {
    try {
      console.log('Attributely v2: Verifying script...');
      const headers = {
        'Authorization': `Bearer ${this.anonKey}`,
        'apikey': this.anonKey,
        'Content-Type': 'application/json',
        'x-client-info': `tracking-script/${this.version}`
      };
      const response = await fetch(
        `${this.apiUrl}/functions/v1/verify-script?websiteId=${this.websiteId}`,
        { 
          method: 'GET',
          headers,
          mode: 'cors',
          credentials: 'omit'
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Attributely v2: Verification failed:', errorData);
        throw new Error(`Verification failed: ${errorData.message || response.statusText}`);
      }
      const data = await response.json();
      if (data.status !== 'verified') {
        throw new Error('Script not verified');
      }
      // Store excluded_urls from verification response
      this.excludedUrls = Array.isArray(data.excluded_urls) ? data.excluded_urls : [];
      // Check if current page is excluded
      if (this.isCurrentPageExcluded()) {
        console.warn('Attributely v2: Current page is excluded from tracking:', window.location.href);
        throw new Error('Current page is excluded from tracking');
      }
      console.log('Attributely v2: Script verified successfully');
    } catch (error) {
      console.error('Attributely v2: Script verification failed:', error);
      throw error;
    }
  }

  // Helper to normalize and check if current page is excluded
  protected isCurrentPageExcluded(): boolean {
    const normalizeUrl = (url: string) => {
      url = url.toLowerCase();
      url = url.replace(/^https?:\/\//, '');
      url = url.replace(/^www\./, '');
      url = url.replace(/\/$/, '');
      return url;
    };
    const normalizedPageUrl = normalizeUrl(window.location.href);
    return this.excludedUrls.some(excluded => {
      if (!excluded) return false;
      const normalizedExcluded = normalizeUrl(excluded);
      return normalizedPageUrl.includes(normalizedExcluded);
    });
  }

  private initStorage(): void {
    console.log('Attributely v2: Initializing storage');
    // Load journey data if it exists
    const journeyData = this.getJourneyData();
    if (journeyData) {
      this.journey = journeyData;
      console.log(`Attributely v2: Loaded journey with ${this.journey.length} entries`);
    }
  }

  private init(): void {
    console.log('Attributely v2: Starting initialization');
    const stored = this.getStoredData();
    const currentHasNewAcquisition = this.hasNewAcquisition();
    const now = new Date().toISOString();

    if (stored) {
      console.log('Attributely v2: Found stored attribution data:', stored);
      this.attributionData = stored;
      
      // Update visit count
      this.attributionData.visitCount = (this.attributionData.visitCount || 1) + 1;
      
      if (currentHasNewAcquisition) {
        console.log('Attributely v2: New acquisition detected, updating LAST TOUCH fields');
        const currentAnalysis = this.analyzeVisit();
        
        // Create a new journey entry
        const queryParams = new URLSearchParams(window.location.search);
        const journeyEntry: JourneyEntry = {
          timestamp: now,
          channel: currentAnalysis.channel,
          source: currentAnalysis.attribution_source,
          medium: currentAnalysis.attribution_medium,
          campaign: currentAnalysis.attribution_campaign,
          term: currentAnalysis.attribution_term,
          content: currentAnalysis.attribution_content,
          landingPage: currentAnalysis.landingPage,
          referrerURL: currentAnalysis.referrerURL,
          gclid: currentAnalysis.gclid,
          msclkid: currentAnalysis.msclkid,
          fbclid: currentAnalysis.fbclid,
          gbraid: currentAnalysis.gbraid,
          wbraid: currentAnalysis.wbraid,
          pageViews: [{
            url: window.location.href,
            timestamp: now
          }]
        };
        
        // Add to journey
        this.journey.push(journeyEntry);
        this.saveJourneyData(this.journey);
        
        // Update attribution data
        this.attributionData = { 
          ...this.attributionData, 
          channel: currentAnalysis.channel,
          attribution_source: currentAnalysis.attribution_source,
          attribution_medium: currentAnalysis.attribution_medium,
          attribution_campaign: currentAnalysis.attribution_campaign,
          attribution_term: currentAnalysis.attribution_term,
          attribution_content: currentAnalysis.attribution_content,
          landingPage: currentAnalysis.landingPage,
          landingpagefolder: currentAnalysis.landingpagefolder,
          lastLandingPage: currentAnalysis.landingPage,
          referrerURL: currentAnalysis.referrerURL,
          lastReferrerURL: currentAnalysis.referrerURL,
          gclid: currentAnalysis.gclid,
          msclkid: currentAnalysis.msclkid,
          fbclid: currentAnalysis.fbclid,
          gbraid: currentAnalysis.gbraid,
          wbraid: currentAnalysis.wbraid,
          lastViewedURL: window.location.href,
          lastSeen: now,
          first_landingpage: currentAnalysis.landingPage,
          first_landingpagefolder: currentAnalysis.landingpagefolder,
        };
      } else {
        console.log('Attributely v2: No new acquisition, updating lastSeen and lastViewedURL only');
        this.attributionData.lastViewedURL = window.location.href;
        this.attributionData.lastSeen = now;
        
        // Add page view to the latest journey entry
        if (this.journey.length > 0) {
          const latestJourney = this.journey[this.journey.length - 1];
          latestJourney.pageViews.push({
            url: window.location.href,
            timestamp: now
          });
          this.saveJourneyData(this.journey);
        }
      }
    } else {
      console.log('Attributely v2: No stored data found, analyzing visit');
      const data = this.analyzeVisit();
      
      // Create a new journey entry
      const queryParams = new URLSearchParams(window.location.search);
      const journeyEntry: JourneyEntry = {
        timestamp: now,
        channel: data.channel,
        source: data.attribution_source,
        medium: data.attribution_medium,
        campaign: data.attribution_campaign,
        term: data.attribution_term,
        content: data.attribution_content,
        landingPage: data.landingPage,
        referrerURL: data.referrerURL,
        gclid: data.gclid,
        msclkid: data.msclkid,
        fbclid: data.fbclid,
        gbraid: data.gbraid,
        wbraid: data.wbraid,
        pageViews: [{
          url: window.location.href,
          timestamp: now
        }]
      };
      
      // Add to journey
      this.journey.push(journeyEntry);
      this.saveJourneyData(this.journey);
      
      // Create attribution data
      this.attributionData = {
        ...data,
        // Set first touch fields
        first_channel: data.channel,
        first_source: data.attribution_source,
        first_medium: data.attribution_medium,
        first_campaign: data.attribution_campaign,
        first_term: data.attribution_term,
        first_content: data.attribution_content,
        first_landingpage: data.landingPage,
        first_landingpagefolder: data.landingpagefolder,
        // Set last touch fields
        lastViewedURL: window.location.href,
        lastSeen: now,
        // Set journey tracking fields
        journeyId: this.generateUUID(),
        visitCount: 1
      };
    }

    this.saveData(this.attributionData!);
    this.populateAllForms();
    this.populateIframeForms();
  
    // Expose tracking data globally for integrations
    window.AttributelyData = this.attributionData;
    window.AttributelyJourney = this.journey;
    console.log('Attributely v2: Global tracking data set', {
      data: window.AttributelyData,
      journey: window.AttributelyJourney
    });
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  private hasNewAcquisition(): boolean {
    const params = new URLSearchParams(window.location.search);
    const hasUTM = Array.from(params.keys()).some(key => key.toLowerCase().startsWith('utm_'));
    const referrer = document.referrer;
    const isExternal = referrer ? !this.isOwnDomain(referrer) : false;
    return hasUTM || isExternal;
  }

  private getUtmParams(): Record<string, string> {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_term: params.get('utm_term') || '',
      utm_content: params.get('utm_content') || ''
    };
  }

  private hasUtmParams(utmParams: Record<string, string>): boolean {
    return Object.values(utmParams).some(val => val !== '');
  }

  private analyzeVisit(): AttributionDataV2 {
    console.log('Attributely v2: Analyzing visit');
    const utmParams = this.getUtmParams();
    const referrer = document.referrer;
    const landingPage = window.location.href;
    // Clean landing page URL (no query or hash)
    let cleanLandingPage = landingPage;
    try {
      const urlObj = new URL(landingPage);
      cleanLandingPage = urlObj.origin + urlObj.pathname;
    } catch {}
    const queryParams = new URLSearchParams(window.location.search);

    // Initialize with raw UTM values
    let attribution_source = utmParams.utm_source;
    let attribution_medium = utmParams.utm_medium;
    const attribution_campaign = utmParams.utm_campaign;
    const attribution_term = utmParams.utm_term;
    const attribution_content = utmParams.utm_content;

    // Default channel is direct
    let channel = this.standardizedChannels.DIRECT;

    // --- AI Search Channel Detection ---
    if (referrer) {
      try {
        const refUrl = new URL(referrer);
        const refHost = refUrl.hostname.toLowerCase();
        if (this.aiSearchDomains.some(domain => refHost === domain || refHost.endsWith('.' + domain))) {
          channel = this.standardizedChannels.AI_SEARCH;
          attribution_source = refHost;
          attribution_medium = 'generative ai';
          console.log('Attributely v2: Detected AI Search channel.');
        }
      } catch {}
    }
    // Prevent channel from being overwritten if already AI Search
    if (channel === this.standardizedChannels.AI_SEARCH) {
      const now = new Date().toISOString();
      const analyzedData: AttributionDataV2 = {
        channel,
        attribution_source: attribution_source,
        attribution_medium: attribution_medium,
        attribution_campaign: attribution_campaign,
        attribution_term: attribution_term,
        attribution_content: attribution_content,
        landingPage,
        lastLandingPage: landingPage,
        referrerURL: referrer,
        lastReferrerURL: referrer,
        lastViewedURL: window.location.href,
        landingpagefolder: '/',
        first_landingpage: cleanLandingPage,
        first_landingpagefolder: '/',
        firstSeen: now,
        lastSeen: now,
        customFields: {},
        gclid: queryParams.get('gclid') || '',
        msclkid: queryParams.get('msclkid') || '',
        fbclid: queryParams.get('fbclid') || '',
        gbraid: queryParams.get('gbraid') || '',
        wbraid: queryParams.get('wbraid') || '',
        websiteId: this.websiteId
      };
      console.log('Attributely v2: Final analyzed data:', analyzedData);
      return analyzedData;
    }

    // Email Channel
    const emailMediums = [
      'email', 'email_marketing', 'emailmarketing', 'newsletter', 'drip', 'mail', 'mailing', 'mailshot', 'mailchimp', 'campaignmonitor', 'constantcontact'
    ];

    if (
      emailMediums.some(val => attribution_medium.toLowerCase().includes(val)) ||
      emailMediums.some(val => attribution_source.toLowerCase().includes(val))
    ) {
      channel = this.standardizedChannels.EMAIL;
      console.log('Attributely v2: Detected Email channel (robust match).');
    }
    // Paid Search Channel
    else if (this.isPaidSearch(utmParams)) {
      channel = this.standardizedChannels.PAID_SEARCH;
      console.log('Attributely v2: Detected Paid Search channel.');
    }
    // Paid Social Channel
    else if (this.isPaidSocial(utmParams)) {
      channel = this.standardizedChannels.PAID_SOCIAL;
      console.log('Attributely v2: Detected Paid Social channel.');
    }
    // Other Campaigns (with UTM parameters)
    else if (this.hasUtmParams(utmParams)) {
      channel = this.standardizedChannels.OTHER_CAMPAIGNS;
      console.log('Attributely v2: Detected Other Campaign channel.');
    }
    // Organic Search
    else if (this.isOrganicSearch(referrer)) {
      channel = this.standardizedChannels.ORGANIC_SEARCH;
      attribution_source = this.getSearchEngine(referrer);
      console.log('Attributely v2: Detected Organic Search channel.');
    }
    // Organic Social
    else if (this.isOrganicSocial(referrer)) {
      channel = this.standardizedChannels.ORGANIC_SOCIAL;
      console.log('Attributely v2: Detected Organic Social channel.');
    }
    // Referral
    else if (referrer && !this.isOwnDomain(referrer)) {
      channel = this.standardizedChannels.REFERRAL;
      console.log('Attributely v2: Detected Referral channel.');
    }

    // Affiliate Channel Detection
    const urlParams = new URLSearchParams(window.location.search);
    let affiliateId = urlParams.get('aff_id') || urlParams.get('partner_id');
    if (affiliateId) {
      channel = 'Affiliate';
      attribution_source = affiliateId;
      attribution_medium = 'partner';
      console.log('Attributely v2: Detected Affiliate channel.');
    }

    let landingpagefolder = '/';
    try {
      const urlObj = new URL(landingPage);
      const path = urlObj.pathname;
      const segments = path.split('/').filter(Boolean);
      // Only set folder if there is a second segment (i.e., /folder/something)
      if (segments.length >= 2) {
        landingpagefolder = `/${segments[0]}`;
      } else {
        landingpagefolder = '/';
      }
    } catch {
      landingpagefolder = '/';
    }

    const now = new Date().toISOString();
    const analyzedData: AttributionDataV2 = {
      // Channel identification
      channel,
      
      // Raw UTM parameters
      attribution_source: attribution_source,
      attribution_medium: attribution_medium,
      attribution_campaign: attribution_campaign,
      attribution_term: attribution_term,
      attribution_content: attribution_content,
      
      // Page information
      landingPage,
      lastLandingPage: landingPage,
      referrerURL: referrer,
      lastReferrerURL: referrer,
      lastViewedURL: window.location.href,
      landingpagefolder,
      first_landingpage: cleanLandingPage,
      first_landingpagefolder: landingpagefolder,
      
      // Timestamps
      firstSeen: now,
      lastSeen: now,
      
      // Custom fields
      customFields: {},
      
      // Click IDs
      gclid: queryParams.get('gclid') || '',
      msclkid: queryParams.get('msclkid') || '',
      fbclid: queryParams.get('fbclid') || '',
      gbraid: queryParams.get('gbraid') || '',
      wbraid: queryParams.get('wbraid') || '',
      
      // Website ID
      websiteId: this.websiteId
    };

    console.log('Attributely v2: Final analyzed data:', analyzedData);
    return analyzedData;
  }

  private isPaidSearch(utmParams: Record<string, string>): boolean {
    const paidSearchTerms = ['adword', 'ppc', 'cpc', 'paidsearch', 'display', 'banner', 'retargeting', 'programmatic', 'adroll', 'criteo', 'gdn', 'media', 'native', 'gads', 'googlads', 'bingads', 'sponsored', 'sem', 'searchads', 'pmax', 'performance_max'];
    return !!(
      paidSearchTerms.some(term => 
        utmParams.utm_medium.toLowerCase().includes(term) || 
        utmParams.utm_source.toLowerCase().includes(term) ||
        utmParams.utm_campaign.toLowerCase().includes(term)
      ) ||
      new URLSearchParams(window.location.search).has('gclid') ||
      new URLSearchParams(window.location.search).has('msclkid')
    );
  }

  private isPaidSocial(utmParams: Record<string, string>): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const socialClickIds = ['fbclid', 'igclid', 'twclid', 'li_fat_id', 'ttclid'];
    const hasSocialClickId = socialClickIds.some(id => urlParams.has(id));
    const paidSocialTerms = ['paidsocial', 'cpc', 'ppc', 'paid', 'sponsored', 'paid_social', 'social_paid', 'cpm', 'ads', 'display', 'banner', 'retargeting', 'programmatic', 'adroll', 'criteo', 'gdn', 'media', 'native'];

    return hasSocialClickId ||
      paidSocialTerms.some(term => utmParams.utm_medium.toLowerCase().includes(term)) &&
      this.socialNetworks.some(network =>
        utmParams.utm_source.toLowerCase().includes(network.split('.')[0])
      );
  }

  private isOrganicSearch(referrer: string): boolean {
    if (!referrer) return false;
    return !!this.searchEngines.some(se => referrer.toLowerCase().includes(se.domain));
  }

  private isOrganicSocial(referrer: string): boolean {
    if (!referrer) return false;
    return !!this.socialNetworks.some(sn => referrer.toLowerCase().includes(sn));
  }

  private getSearchEngine(referrer: string): string {
    try {
      const lower = referrer.toLowerCase();
      const engine = this.searchEngines.find(se => lower.includes(se.domain));
      const result = engine ? engine.name : new URL(referrer).hostname;
      return result;
    } catch (error) {
      console.error('Attributely v2: Error in getSearchEngine:', error);
      return '';
    }
  }

  private getSocialNetwork(referrer: string): string {
    try {
      const hostname = new URL(referrer).hostname;
      const network = this.socialNetworks.find(sn => hostname.toLowerCase().includes(sn));
      return network ? network.split('.')[0] : hostname;
    } catch (error) {
      console.error('Attributely v2: Error in getSocialNetwork:', error);
      return '';
    }
  }

  private getSearchKeyword(referrer: string): string | null {
    try {
      const url = new URL(referrer);
      for (const key of ['q', 'query', 'search', 'text']) {
        const value = url.searchParams.get(key);
        if (value) return value;
      }
    } catch (error) {
      console.error('Attributely v2: Error in getSearchKeyword:', error);
    }
    return null;
  }

  private isOwnDomain(url: string): boolean {
    try {
      const getRoot = (hostname: string) => {
        const parts = hostname.split('.');
        return parts.length > 1 ? parts.slice(-2).join('.') : hostname;
      };
      const urlRoot = getRoot(new URL(url).hostname);
      const currentRoot = getRoot(window.location.hostname);
      return urlRoot === currentRoot;
    } catch (error) {
      console.error('Attributely v2: Error in isOwnDomain:', error);
      return false;
    }
  }

  private convertDataToLowerCase(data: AttributionDataV2): AttributionDataV2 {
    const lowercasedData: AttributionDataV2 = { ...data };
    // Convert all direct string properties
    for (const key in lowercasedData) {
      if (typeof lowercasedData[key] === 'string') {
        lowercasedData[key] = lowercasedData[key].toLowerCase();
      }
    }
    // Convert nested customFields
    if (lowercasedData.customFields) {
      const newCustomFields: Record<string, string> = {};
      for (const key in lowercasedData.customFields) {
        newCustomFields[key] = lowercasedData.customFields[key].toLowerCase();
      }
      lowercasedData.customFields = newCustomFields;
    }
    return lowercasedData;
  }

  protected saveData(data: AttributionDataV2): void {
    try {
      if (!data) return;
      // Clean landing page fields before saving
      ['landingPage', 'first_landingpage', 'lastLandingPage'].forEach(key => {
        if ((data as any)[key]) (data as any)[key] = cleanUrl((data as any)[key]);
      });
      console.log('Attributely v2: Converting attribution data to lowercase before saving to cookie.');
      const lowerCaseData = this.convertDataToLowerCase(data);
      const expires = new Date();
      expires.setDate(expires.getDate() + this.cookieExpiryDays);
      const jsonData = JSON.stringify(lowerCaseData);
      
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      const rootDomain = parts.length > 1 ? parts.slice(-2).join('.') : hostname;
      
      const cookieValue = encodeURIComponent(jsonData);
      const cookie = [
        `${this.storageKey}=${cookieValue}`,
        `expires=${expires.toUTCString()}`,
        'path=/',
        `domain=.${rootDomain}`,
        'SameSite=Lax',
        window.location.protocol === 'https:' ? 'Secure' : ''
      ].filter(Boolean).join('; ');
      
      document.cookie = cookie;
      console.log(`Attributely v2: Set cookie for domain .${rootDomain}`, {
        cookieKey: this.storageKey,
        cookieExpiry: expires.toUTCString(),
        dataSize: jsonData.length,
        rootDomain: rootDomain
      });
    } catch (error) {
      console.error('Attributely v2: Error setting cookie:', error);
    }
  }

  private getStoredData(): AttributionDataV2 | null {
    const match = document.cookie.match(new RegExp('(^| )' + this.storageKey + '=([^;]+)'));
    
    if (match) {
      try {
        const data = JSON.parse(decodeURIComponent(match[2]));
        console.log('Attributely v2: Retrieved stored data from cookie', {
          cookieKey: this.storageKey,
          dataSize: match[2].length,
          hasFirstTouch: !!data.first_channel,
          hasLastTouch: !!data.channel
        });
        return data;
      } catch (error) {
        console.error('Attributely v2: Error parsing cookie data:', error);
        return null;
      }
    }
    console.log('Attributely v2: No stored attribution data found');
    return null;
  }

  private saveJourneyData(journey: JourneyEntry[]): void {
    try {
      const expires = new Date();
      expires.setDate(expires.getDate() + this.cookieExpiryDays);
      const jsonData = JSON.stringify(journey);
      
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      const rootDomain = parts.length > 1 ? parts.slice(-2).join('.') : hostname;
      
      const cookieValue = encodeURIComponent(jsonData);
      const cookie = [
        `${this.journeyKey}=${cookieValue}`,
        `expires=${expires.toUTCString()}`,
        'path=/',
        `domain=.${rootDomain}`,
        'SameSite=Lax',
        window.location.protocol === 'https:' ? 'Secure' : ''
      ].filter(Boolean).join('; ');
      
      document.cookie = cookie;
      console.log(`Attributely v2: Saved journey data for domain .${rootDomain}`, {
        journeyEntries: journey.length,
        dataSize: jsonData.length
      });
    } catch (error) {
      console.error('Attributely v2: Error saving journey data:', error);
    }
  }

  private getJourneyData(): JourneyEntry[] | null {
    const match = document.cookie.match(new RegExp('(^| )' + this.journeyKey + '=([^;]+)'));
    
    if (match) {
      try {
        const data = JSON.parse(decodeURIComponent(match[2]));
        console.log('Attributely v2: Retrieved journey data from cookie', {
          journeyEntries: data.length
        });
        return data;
      } catch (error) {
        console.error('Attributely v2: Error parsing journey data:', error);
        return null;
      }
    }
    console.log('Attributely v2: No journey data found');
    return null;
  }

  private trackPageView(): void {
    // Update landingPage to current page on every pageview
    if (this.attributionData) {
      this.attributionData.landingPage = window.location.href;
      this.saveData(this.attributionData);
    }
  }

  private setupPageViewTracking(): void {
    // Call trackPageView on initial load
    this.trackPageView();
    // Optionally, add listeners for SPA navigation if needed
    // window.addEventListener('popstate', () => this.trackPageView());
    // window.addEventListener('pushstate', () => this.trackPageView());
  }

  private updateInputValue(input: HTMLInputElement, value: string): void {
    const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
    if (nativeSetter) {
      nativeSetter.call(input, value);
    } else {
      input.value = value;
    }
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  private populateExplicitFields(form: HTMLFormElement): void {
    if (!this.attributionData) return;
    
    const mapping: Record<string, keyof AttributionDataV2> = {
      // Channel identification
      'channel': 'channel',
      
      // Raw UTM parameters
      'attribution_source': 'attribution_source',
      'attribution_medium': 'attribution_medium',
      'attribution_campaign': 'attribution_campaign',
      'attribution_term': 'attribution_term',
      'attribution_content': 'attribution_content',
      
      // Page information
      'landingpage': 'landingPage',
      'lastlandingpage': 'lastLandingPage',
      'referrerurl': 'referrerURL',
      'lastreferrerurl': 'lastReferrerURL',
      'lastviewedurl': 'lastViewedURL',
      'landingpagefolder': 'landingpagefolder',
      
      // Timestamps
      'firstseen': 'firstSeen',
      'lastseen': 'lastSeen',
      
      // Click IDs
      'gclid': 'gclid',
      'msclkid': 'msclkid',
      'fbclid': 'fbclid',
      'gbraid': 'gbraid',
      'wbraid': 'wbraid',
      
      // First touch data
      'first_channel': 'first_channel',
      'first_source': 'first_source',
      'first_medium': 'first_medium',
      'first_campaign': 'first_campaign',
      'first_term': 'first_term',
      'first_content': 'first_content',
      
      // Journey tracking
      'journeyid': 'journeyId',
      'visitcount': 'visitCount',
      
      // Contact information
      'email': 'email'
    };

    const explicitInputs = form.querySelectorAll('input[data-attribution], input[name="attribution_data"], input[name="attribution_journey"]');
    explicitInputs.forEach(input => {
      if (input instanceof HTMLInputElement) {
        const key = input.getAttribute('data-attribution')?.toLowerCase();
        if (key && mapping[key] && this.attributionData) {
          const value = this.attributionData[mapping[key]] || '';
          this.updateInputValue(input, value as string);
        }
        // Autofill attribution_data
        if (input.name === 'attribution_data') {
          const data = this.getStoredData();
          if (data) this.updateInputValue(input, JSON.stringify(data));
        }
        // Autofill attribution_journey
        if (input.name === 'attribution_journey') {
          const journey = this.getJourneyData();
          if (journey) this.updateInputValue(input, JSON.stringify(journey));
        }
      }
    });
  }

  private populateFallbackFields(form: HTMLFormElement): void {
    if (!this.attributionData) return;

    const fieldMappings = [
      // Channel identification
      { default: '[channel]', key: 'channel' },
      
      // Raw UTM parameters
      { default: '[attribution_source]', key: 'attribution_source' },
      { default: '[attribution_medium]', key: 'attribution_medium' },
      { default: '[attribution_campaign]', key: 'attribution_campaign' },
      { default: '[attribution_term]', key: 'attribution_term' },
      { default: '[attribution_content]', key: 'attribution_content' },
      
      // Page information
      { default: '[landingpage]', key: 'landingPage' },
      { default: '[last_landing_page]', key: 'lastLandingPage' },
      { default: '[referrer_url]', key: 'referrerURL' },
      { default: '[last_referrer_url]', key: 'lastReferrerURL' },
      { default: '[last_viewed_url]', key: 'lastViewedURL' },
      { default: '[landingpagefolder]', key: 'landingpagefolder' },
      
      // Timestamps
      { default: '[first_seen]', key: 'firstSeen' },
      { default: '[last_seen]', key: 'lastSeen' },
      
      // Click IDs
      { default: '[gclid]', key: 'gclid' },
      { default: '[msclkid]', key: 'msclkid' },
      { default: '[fbclid]', key: 'fbclid' },
      { default: '[gbraid]', key: 'gbraid' },
      { default: '[wbraid]', key: 'wbraid' },
      
      // First touch data
      { default: '[first_channel]', key: 'first_channel' },
      { default: '[first_source]', key: 'first_source' },
      { default: '[first_medium]', key: 'first_medium' },
      { default: '[first_campaign]', key: 'first_campaign' },
      { default: '[first_term]', key: 'first_term' },
      { default: '[first_content]', key: 'first_content' },
      
      // Journey tracking
      { default: '[journey_id]', key: 'journeyId' },
      { default: '[visit_count]', key: 'visitCount' }
    ];

    const inputs = form.querySelectorAll('input');
    inputs.forEach(input => {
      if (input instanceof HTMLInputElement) {
        // Autofill attribution_data
        if (input.name === 'attribution_data') {
          const data = this.getStoredData();
          if (data) this.updateInputValue(input, JSON.stringify(data));
        }
        // Autofill attribution_journey
        if (input.name === 'attribution_journey') {
          const journey = this.getJourneyData();
          if (journey) this.updateInputValue(input, JSON.stringify(journey));
        }
        const defaultValue = input.defaultValue || input.value;
        const mapping = fieldMappings.find(m => defaultValue.includes(m.default));
        if (mapping && this.attributionData) {
          const value = this.attributionData[mapping.key as keyof AttributionDataV2];
          if (value) {
            const newValue = defaultValue.replace(mapping.default, value.toString());
            this.updateInputValue(input, newValue);
          }
        }
        
        // Check if this is an email field and store it in attribution data
        if ((input.type === 'email' || input.name.toLowerCase().includes('email')) && input.value) {
          this.attributionData.email = input.value;
          this.saveData(this.attributionData);
        }
      }
    });
  }

  private populateAllForms(): void {
    const forms = document.querySelectorAll('form');
    forms.forEach(form => {
      if (form instanceof HTMLFormElement) {
        this.populateExplicitFields(form);
        this.populateFallbackFields(form);
        this.setupFormTracking(form);
      }
    });
  }

  private populateIframeForms(): void {
    // Handle Calendly
    if (typeof Calendly !== 'undefined') {
      const prefill = {
        customAnswers: {
          a1: this.attributionData?.channel || '',
          a2: this.attributionData?.attribution_source || '',
          a3: this.attributionData?.attribution_medium || '',
          a4: this.attributionData?.attribution_campaign || '',
          a5: this.attributionData?.attribution_term || '',
          a6: this.attributionData?.attribution_content || '',
          a7: this.attributionData?.landingPage || '',
          a8: this.attributionData?.referrerURL || ''
        }
      };
      
      // Store prefill data in window object for Calendly to access
      (window as any).CalendlyPrefillData = prefill;
    }
  }
  
  protected getBrowserInfo(): { browser: string, deviceType: string, operatingSystem: string } {
    const userAgent = navigator.userAgent;
    let browser = 'Unknown';
    let deviceType = 'Unknown';
    let operatingSystem = 'Unknown';
    
    // Detect browser
    if (userAgent.indexOf('Firefox') > -1) {
      browser = 'Firefox';
    } else if (userAgent.indexOf('SamsungBrowser') > -1) {
      browser = 'Samsung Internet';
    } else if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
      browser = 'Opera';
    } else if (userAgent.indexOf('Trident') > -1) {
      browser = 'Internet Explorer';
    } else if (userAgent.indexOf('Edge') > -1) {
      browser = 'Edge';
    } else if (userAgent.indexOf('Chrome') > -1) {
      browser = 'Chrome';
    } else if (userAgent.indexOf('Safari') > -1) {
      browser = 'Safari';
    }
    
    // Detect device type
    if (/Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      deviceType = /iPad|tablet|Tablet/i.test(userAgent) ? 'Tablet' : 'Mobile';
    } else {
      deviceType = 'Desktop';
    }
    
    // Detect operating system
    if (/Windows/i.test(userAgent)) {
      operatingSystem = 'Windows';
    } else if (/Macintosh|MacIntel|MacPPC|Mac68K/i.test(userAgent)) {
      operatingSystem = 'MacOS';
    } else if (/Android/i.test(userAgent)) {
      operatingSystem = 'Android';
    } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
      operatingSystem = 'iOS';
    } else if (/Linux/i.test(userAgent)) {
      operatingSystem = 'Linux';
    }
    
    return { browser, deviceType, operatingSystem };
  }
  
  private setupFormTracking(form: HTMLFormElement): void {
    console.log('Attributely v2: Setting up form tracking for:', form);
    
    form.addEventListener('submit', (event) => {
      console.log('Attributely v2: Form submission detected, capturing field data');
      
      // Capture all form field data
      const formFields: Record<string, string> = {};
      const formData = new FormData(form);
      
      // Process all form elements to capture visible fields
      Array.from(form.elements).forEach((element) => {
        if (element instanceof HTMLInputElement || 
            element instanceof HTMLSelectElement || 
            element instanceof HTMLTextAreaElement) {
          // Robust hidden field exclusion
          const hiddenPatterns = [
            'hidden', 'hiddenfield', 'hidden-field', 'hiddenfields', 'hidden_fields',
            '_hidden', 'hidden_', '_wp_', '_nonce', 'action', 'step', 'instance_id', 'post_id', 'form_id'
          ];
          const nameLower = (element.name || '').toLowerCase();
          const idLower = (element.id || '').toLowerCase();
          const typeLower = (element instanceof HTMLInputElement && element.type) ? element.type.toLowerCase() : '';
          if (
            (element instanceof HTMLInputElement && typeLower === 'hidden') ||
            hiddenPatterns.some(pattern => nameLower.includes(pattern) || idLower.includes(pattern))
          ) {
            return; // Exclude this field
          }
          
          // Skip password fields for privacy
          if (element instanceof HTMLInputElement && element.type === 'password') {
            return;
          }
          
          // Skip submit buttons
          if (element instanceof HTMLInputElement && element.type === 'submit') {
            return;
          }
          
          // Skip hidden fields
          if (element instanceof HTMLInputElement && 
              (element.type === 'hidden' || 
               element.name.startsWith('hidden_') || 
               element.name.includes('_hidden') || 
               element.name.includes('_wp_') || 
               element.name.includes('_nonce') || 
               element.name === 'action' || 
               element.name === 'step' || 
               element.name === 'instance_id' || 
               element.name.includes('post_id') || 
               element.name.includes('form_id'))) {
            return;
          }
          
          // Get field name (prefer label, then name, then id, then placeholder)
          let fieldName = '';
          
          // Try to find associated label
          const labelElement = element.id ? 
            document.querySelector(`label[for="${element.id}"]`) : null;
          
          if (labelElement && labelElement.textContent) {
            fieldName = labelElement.textContent.trim();
          } else {
            // Try to find a parent label
            const parentLabel = element.closest('label');
            if (parentLabel && parentLabel.textContent) {
              // Get text content excluding the text of any inputs
              const inputs = parentLabel.querySelectorAll('input, select, textarea');
              let labelText = parentLabel.textContent;
              inputs.forEach(input => {
                const inputEl = input as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
                if (input !== element && inputEl.value) {
                  labelText = labelText.replace(inputEl.value, '');
                }
              });
              fieldName = labelText.trim();
            }
          }
          
          // If no label found, use name or id
          if (!fieldName) {
            fieldName = element.name || element.id || element.getAttribute('placeholder') || 'unnamed_field';
          }
          
          // Get field value
          let fieldValue = '';
          if (element instanceof HTMLSelectElement) {
            fieldValue = element.value;
          } else if (element instanceof HTMLInputElement) {
            if (element.type === 'checkbox' || element.type === 'radio') {
              fieldValue = element.checked ? (element.value || 'checked') : '';
              // Only include checked checkboxes and radios
              if (!element.checked) return;
            } else {
              fieldValue = element.value;
            }
          } else if (element instanceof HTMLTextAreaElement) {
            fieldValue = element.value;
          }
          
          // Store field data if it has a value
          if (fieldValue) {
            formFields[fieldName] = fieldValue;
            
            // If this is an email field, also update attribution data
            if ((element.type === 'email' || 
                 fieldName.toLowerCase().includes('email')) && 
                fieldValue && 
                this.attributionData) {
              this.attributionData.email = fieldValue;
            }
          }
        }
      });
      
      // Add form fields to attribution data
      if (this.attributionData) {
        this.attributionData.formFields = formFields;
        this.saveData(this.attributionData);
      }
      
      console.log('Attributely v2: Captured form fields:', formFields);
      
      // Get email from form if available
      let email = '';
      const emailInputs = form.querySelectorAll('input[type="email"], input[name*="email"], input[placeholder*="email"], input[id*="email"]');
      emailInputs.forEach(input => {
        if (input instanceof HTMLInputElement && input.value && !email) {
          email = input.value;
          // Update attribution data with email
          if (this.attributionData) {
            this.attributionData.email = email;
            this.saveData(this.attributionData);
          }
        }
      });
      
      // Get browser information
      const { browser, deviceType, operatingSystem } = this.getBrowserInfo();
      
      // Send tracking data to server
      (async () => {
        try {
          if (!this.attributionData) return;
          
          const trackingPayload = {
            websiteId: this.websiteId,
            timestamp: new Date().toISOString(),
            formId: form.id || form.getAttribute('data-form-id') || 'unknown',
            pageUrl: window.location.href,
            version: 'v2',
            attribution: this.attributionData,
            formFields: this.attributionData?.formFields || {},
            browser,
            deviceType: deviceType,
            operatingSystem: operatingSystem,
            journey: this.journey
          };
          
          console.log('Attributely v2: Sending tracking payload:', trackingPayload);
          
          const response = await fetch(`${this.apiUrl}/functions/v1/track-v2`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${this.anonKey}`,
              'apikey': this.anonKey,
              'Content-Type': 'application/json',
              'x-client-info': `tracking-script/${this.version}`
            },
            body: JSON.stringify(trackingPayload)
          });
          
          if (!response.ok) {
            let errorText = '';
            try {
              errorText = await response.text();
              console.error('Attributely v2: API response error:', {
                status: response.status,
                statusText: response.statusText,
                body: errorText
              });
            } catch (e) {
              console.error('Attributely v2: Failed to read error response:', e);
              errorText = response.statusText;
            }
            throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
          }
          
          const responseData = await response.json();
          console.log('Attributely v2: Track API response:', responseData);
        } catch (error) {
          console.error('Attributely v2: Error tracking form submission:', error);
        }
      })();
    });
  }

  private observeForms(): void {
    // Create a MutationObserver to watch for new forms
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              // Check if the added node is a form
              if (node.tagName === 'FORM') {
                this.populateExplicitFields(node as HTMLFormElement);
                this.populateFallbackFields(node as HTMLFormElement);
                this.setupFormTracking(node as HTMLFormElement);
              }
              // Check for forms within the added node
              const forms = node.querySelectorAll('form');
              forms.forEach(form => {
                if (form instanceof HTMLFormElement) {
                  this.populateExplicitFields(form);
                  this.populateFallbackFields(form);
                  this.setupFormTracking(form);
                }
              });
            }
          });
        }
      });
    });

    // Start observing the document with the configured parameters
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Extend AttributelyV2 to handle external form submissions
class AttributelyV2Extended extends AttributelyV2 {
  public handleExternalFormSubmit(formData: Record<string, any>, formId?: string) {
    // Clean landing page fields before sending payload
    ['landingPage', 'first_landingpage', 'lastLandingPage'].forEach(key => {
      if (formData[key]) formData[key] = cleanUrl(formData[key]);
    });
    if (this.attributionData) {
      this.attributionData.formFields = { ...formData };
      this.saveData(this.attributionData);
    }
    this.sendFormTrackingPayload(formData, formId);
  }

  private async sendFormTrackingPayload(formData: Record<string, any>, formId?: string) {
    // Check if current page is excluded before sending tracking data
    if (this.isCurrentPageExcluded()) {
      console.warn('Attributely v2: Skipping tracking for excluded URL:', window.location.href);
      return;
    }
    const { browser, deviceType, operatingSystem } = this.getBrowserInfo();
    const trackingPayload = {
      websiteId: this.websiteId,
      timestamp: new Date().toISOString(),
      formId: formId || 'external-js-form',
      pageUrl: window.location.href,
      version: 'v2',
      attribution: this.attributionData,
      formFields: formData,
      browser,
      deviceType,
      operatingSystem,
      journey: this.journey
    };
    try {
      const response = await fetch(`${this.apiUrl}/functions/v1/track-v2`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.anonKey}`,
          'apikey': this.anonKey,
          'Content-Type': 'application/json',
          'x-client-info': `tracking-script/${this.version}`
        },
        body: JSON.stringify(trackingPayload)
      });
      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
          console.error('Attributely v2: API response error (external form):', {
            status: response.status,
            statusText: response.statusText,
            body: errorText
          });
        } catch (e) {
          errorText = response.statusText;
        }
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      const responseData = await response.json();
      console.log('Attributely v2: Track API response (external form):', responseData);
    } catch (error) {
      console.error('Attributely v2: Error sending external form tracking payload:', error);
    }
  }
}

// --- HubSpot iframe form support: Listen for postMessage from HubSpot forms with buffering for race condition ---
(function () {
  // Use a global buffer for early messages
  window.__attrEarlyQueue = window.__attrEarlyQueue || [];

  function isHubSpotEvent(event: MessageEvent) {
    try {
      const host = new URL(event.origin).hostname;
      // accept same-origin OR *.hsforms.com|net
      if (
        host !== window.location.hostname &&
        !/\.hsforms\.(com|net)$/i.test(host)
      ) return false;
    } catch { return false; }
    return (
      event.data?.type === 'hsFormCallback' &&
      (event.data.eventName === 'onFormSubmit' || event.data.eventName === 'onFormSubmitted')
    );
  }

  // Helper to filter out hidden fields from HubSpot data
  function filterHubSpotFields(data: any): Record<string, any> {
    const formData: Record<string, any> = {};
    if (data.data && Array.isArray(data.data)) {
      data.data.forEach((f: { name: string; value: any; type?: string }) => {
        // Exclude hidden fields (type === 'hidden' or name includes 'hidden')
        if ((f.type && f.type === 'hidden') || (f.name && f.name.toLowerCase().includes('hidden'))) return;
        formData[f.name] = f.value;
      });
    }
    return formData;
  }

  window.addEventListener('message', (event) => {
    if (isHubSpotEvent(event)) {
      const formData = filterHubSpotFields(event.data);
      if (typeof window.AttributelyFormSubmit === 'function') {
        window.AttributelyFormSubmit(formData, event.data.formId || 'external-js-form');
      } else {
        if (!window.__attrEarlyQueue) window.__attrEarlyQueue = [];
        window.__attrEarlyQueue.push({ fields: formData, formId: event.data.formId || 'external-js-form' });
      }
    }
  });
})();

// --- JotForm iframe form support: Listen for postMessage from JotForm forms with buffering for race condition ---
(function () {
  window.__jotformEarlyQueue = window.__jotformEarlyQueue || [];

  // Helper to flatten JotForm fields, excluding hidden fields (if possible)
  function flattenJotFormFields(jotFormData: any): Record<string, any> {
    // If jotFormData is already a flat object, just return it
    // If it contains a fields array, flatten it (customize as needed)
    if (jotFormData && typeof jotFormData === 'object' && !Array.isArray(jotFormData)) {
      return jotFormData;
    }
    return {};
  }

  // Type guard to check if event is a MessageEvent
  function isMessageEvent(event: any): event is MessageEvent {
    return typeof event === 'object' && event !== null && 'origin' in event && 'data' in event;
  }

  window.addEventListener('message', (event) => {
    try {
      const host = new URL(event.origin).hostname;
      if (host.includes('jotform.com') && event.data && event.data.action === 'submission-completed') {
        const formData = event.data.submissionData;
        if (typeof window.AttributelyFormSubmit === 'function') {
          window.AttributelyFormSubmit(formData);
        } else {
          if (!window.__jotformEarlyQueue) window.__jotformEarlyQueue = [];
          window.__jotformEarlyQueue.push(formData);
        }
      }
    } catch {}
  });

  // After Attributely instance creation, flush early messages if any
  (function(w) {
    if (window.__jotformEarlyQueue && Array.isArray(window.__jotformEarlyQueue) && window.__jotformEarlyQueue.length) {
      window.__jotformEarlyQueue.forEach((d) => {
        if (typeof window.AttributelyFormSubmit === 'function') {
          window.AttributelyFormSubmit(d);
        }
      });
      window.__jotformEarlyQueue.length = 0;
    }
  })(window);
})();

// --- Scalable iframe form support: Listen for postMessage from various providers (e.g., Tally, Tidycal) ---
(function () {
  window.__iframeFormEarlyQueue = window.__iframeFormEarlyQueue || [];

  window.addEventListener('message', (event) => {
    if (!event.data) return;

    // --- Tally: event.data is a string ---
    if (typeof event.data === "string" && event.data.includes("Tally.FormSubmitted")) {
      console.log('[Attributely v2][iframe] Message received (Tally):', event);
      let parsed;
      try {
        parsed = JSON.parse(event.data);
        console.log('[Attributely v2][iframe] Successfully parsed Tally event data:', parsed);
      } catch (err) {
        console.error('[Attributely v2][Tally] Failed to parse event.data:', event.data, err);
        return;
      }
      const handler = (window as any).providerHandlers['Tally.FormSubmitted'];
      if (!handler) {
        console.warn('[Attributely v2][Tally] No handler found for Tally.FormSubmitted');
        return;
      }
      const normalized = handler(event, parsed);
      if (!normalized) {
        console.warn('[Attributely v2][Tally] Handler did not return normalized data.');
        return;
      }
      console.log(`[Attributely v2][iframe] Normalized form data for event 'Tally.FormSubmitted':`, normalized);
      if (typeof window.AttributelyFormSubmit === 'function') {
        console.log('[Attributely v2][iframe] Calling AttributelyFormSubmit with normalized data.');
        window.AttributelyFormSubmit(normalized.fields, normalized.formId);
      } else {
        if (!window.__iframeFormEarlyQueue) window.__iframeFormEarlyQueue = [];
        window.__iframeFormEarlyQueue.push({ fields: normalized.fields, formId: normalized.formId });
        console.log('[Attributely v2][iframe] AttributelyFormSubmit not ready, queued normalized data.');
      }
      return;
    }

    // GoHighLevel: event.origin and event.data structure
    if (
      event.origin === "https://api.leadconnectorhq.com" &&
      Array.isArray(event.data) &&
      typeof event.data[2] === "string"
    ) {
      console.log('[Attributely v2][GoHighLevel] Message received:', event);
      const handler = (window as any).providerHandlers['GoHighLevel.FormSubmitted'];
      if (!handler) return;
      const normalized = handler(event);
      if (!normalized) return;
      if (typeof window.AttributelyFormSubmit === 'function') {
        console.log('[Attributely v2][GoHighLevel] Calling AttributelyFormSubmit with:', normalized.fields, normalized.formId);
        window.AttributelyFormSubmit(normalized.fields, normalized.formId);
      } else {
        if (!window.__iframeFormEarlyQueue) window.__iframeFormEarlyQueue = [];
        window.__iframeFormEarlyQueue.push({ fields: normalized.fields, formId: normalized.formId });
        console.log('[Attributely v2][GoHighLevel] AttributelyFormSubmit not ready, queued normalized data.');
      }
      return;
    }

    // Cal.com: bookingSuccessful event
    if (
      event.origin && event.origin.includes('cal.com') &&
      event.data && event.data.type === 'bookingSuccessful'
    ) {
      console.log('[Attributely v2][Cal.com] Message received:', event);
      const handler = (window as any).providerHandlers['CalCom.MeetingBooked'];
      if (!handler) return;
      const normalized = handler(event);
      if (!normalized) return;
      if (typeof window.AttributelyFormSubmit === 'function') {
        console.log('[Attributely v2][Cal.com] Calling AttributelyFormSubmit with:', normalized.fields, normalized.formId);
        window.AttributelyFormSubmit(normalized.fields, normalized.formId);
      } else {
        if (!window.__iframeFormEarlyQueue) window.__iframeFormEarlyQueue = [];
        window.__iframeFormEarlyQueue.push({ fields: normalized.fields, formId: normalized.formId });
        console.log('[Attributely v2][Cal.com] AttributelyFormSubmit not ready, queued normalized data.');
      }
      return;
    }

    // HubSpot: meetingBookSucceeded event
    if (
      event.data && event.data.meetingBookSucceeded === true &&
      event.data.meetingsPayload && event.data.meetingsPayload.bookingResponse
    ) {
      console.log('[Attributely v2][HubSpot] Message received:', event);
      const handler = (window as any).providerHandlers['HubSpot.MeetingBooked'];
      if (!handler) return;
      const normalized = handler(event);
      if (!normalized) return;
      if (typeof window.AttributelyFormSubmit === 'function') {
        console.log('[Attributely v2][HubSpot] Calling AttributelyFormSubmit with:', normalized.fields, normalized.formId);
        window.AttributelyFormSubmit(normalized.fields, normalized.formId);
      } else {
        if (!window.__iframeFormEarlyQueue) window.__iframeFormEarlyQueue = [];
        window.__iframeFormEarlyQueue.push({ fields: normalized.fields, formId: normalized.formId });
        console.log('[Attributely v2][HubSpot] AttributelyFormSubmit not ready, queued normalized data.');
      }
      return;
    }

    // Tidycal: event.data.event === 'bookingComplete' and event.data.data.booking exists
    if (event.data && event.data.event === 'bookingComplete' && event.data.data && event.data.data.booking) {
      console.log('[Attributely v2][Tidycal] Message received:', event);
      const handler = (window as any).providerHandlers['Tidycal.MeetingBooked'];
      if (!handler) return;
      const normalized = handler(event);
      if (!normalized) return;
      if (typeof window.AttributelyFormSubmit === 'function') {
        console.log('[Attributely v2][Tidycal] Calling AttributelyFormSubmit with:', normalized.fields, normalized.formId);
        window.AttributelyFormSubmit(normalized.fields, normalized.formId);
      } else {
        if (!window.__iframeFormEarlyQueue) window.__iframeFormEarlyQueue = [];
        window.__iframeFormEarlyQueue.push({ fields: normalized.fields, formId: normalized.formId });
        console.log('[Attributely v2][Tidycal] AttributelyFormSubmit not ready, queued normalized data.');
      }
      return;
    }

    // --- Other providers: event.data is an object ---
    const eventType = event.data.event || event.data.action;
    if (!eventType) return;
    console.log(`[Attributely v2][iframe] Message received for eventType '${eventType}':`, event);
    const handler = (window as any).providerHandlers[eventType];
    if (!handler) {
      console.warn(`[Attributely v2][iframe] No handler found for eventType '${eventType}'.`);
      return;
    }
    const normalized = handler(event);
    if (!normalized) {
      console.warn(`[Attributely v2][iframe] Handler for eventType '${eventType}' did not return normalized data.`);
      return;
    }
    console.log(`[Attributely v2][iframe] Normalized form data for event '${eventType}':`, normalized);
    if (typeof window.AttributelyFormSubmit === 'function') {
      console.log(`[Attributely v2][iframe] Calling AttributelyFormSubmit with normalized data for event '${eventType}'.`);
      window.AttributelyFormSubmit(normalized.fields, normalized.formId);
    } else {
      if (!window.__iframeFormEarlyQueue) window.__iframeFormEarlyQueue = [];
      window.__iframeFormEarlyQueue.push({ fields: normalized.fields, formId: normalized.formId });
      console.log(`[Attributely v2][iframe] AttributelyFormSubmit not ready, queued normalized data for event '${eventType}'.`);
    }
  });

  // After Attributely instance creation, flush early messages if any
  (function(w) {
    if (window.__iframeFormEarlyQueue && Array.isArray(window.__iframeFormEarlyQueue) && window.__iframeFormEarlyQueue.length) {
      console.log('[Attributely v2][iframe] Flushing early queued iframe form messages:', window.__iframeFormEarlyQueue.length);
      window.__iframeFormEarlyQueue.forEach((d) => {
        if (typeof window.AttributelyFormSubmit === 'function') {
          window.AttributelyFormSubmit(d.fields, d.formId);
        }
      });
      window.__iframeFormEarlyQueue.length = 0;
    }
  })(window);
})();

// After Attributely instance creation, flush early messages if any
(function(w) {
  if (
    w.attributely &&
    Array.isArray(w.attributely) &&
    w.attributely.length > 0 &&
    (typeof w.attributely[0] === 'object' && w.attributely[0] !== null)
  ) {
    try {
      console.log('Attributely v2: Initialization starting');
      let websiteId = null;
      for (const item of w.attributely) {
        if (item && typeof item === 'object' && item.websiteId) {
          websiteId = item.websiteId;
          break;
        }
      }
      if (!websiteId && w.attributely.length > 0) {
        const firstItem = w.attributely[0];
        if (typeof firstItem === 'object' && firstItem.websiteId) {
          websiteId = firstItem.websiteId;
        }
      }
      if (!websiteId) {
        console.error('Attributely v2: Missing website ID. Make sure to initialize with websiteId.');
        return;
      }
      console.log('Attributely v2: Found website ID:', websiteId);
      // Only assign instance if attributely is an object (not an array)
      if (typeof w.attributely === 'object' && !Array.isArray(w.attributely)) {
        (w.attributely as any).instance = new AttributelyV2Extended(websiteId);
      } else if (Array.isArray(w.attributely)) {
        (w as any).attributelyInstance = new AttributelyV2Extended(websiteId);
      }
      // Flush any buffered data
      if (window.__attrEarlyQueue && window.__attrEarlyQueue.length) {
        console.log('[Attributely v2][Flush] Flushing __attrEarlyQueue:', window.__attrEarlyQueue.length);
        window.__attrEarlyQueue.forEach((d) => {
          if (typeof window.AttributelyFormSubmit === 'function') {
            window.AttributelyFormSubmit(d.fields, d.formId);
          }
        });
        window.__attrEarlyQueue.length = 0;
      }
      if (window.__jotformEarlyQueue && window.__jotformEarlyQueue.length) {
        console.log('[Attributely v2][Flush] Flushing __jotformEarlyQueue:', window.__jotformEarlyQueue.length);
        window.__jotformEarlyQueue.forEach((d) => {
          if (typeof window.AttributelyFormSubmit === 'function') {
            window.AttributelyFormSubmit(d);
          }
        });
        window.__jotformEarlyQueue.length = 0;
      }
      if (window.__iframeFormEarlyQueue && window.__iframeFormEarlyQueue.length) {
        console.log('[Attributely v2][Flush] Flushing __iframeFormEarlyQueue:', window.__iframeFormEarlyQueue.length);
        window.__iframeFormEarlyQueue.forEach((d) => {
          if (typeof window.AttributelyFormSubmit === 'function') {
            window.AttributelyFormSubmit(d.fields, d.formId);
          }
        });
        window.__iframeFormEarlyQueue.length = 0;
      }
    } catch (error) {
      console.error('Attributely v2: Initialization error:', error);
    }
  }
})(window);

// --- Cognito Forms support: Listen for afterSubmit event and forward to Attributely ---
(function () {
  // Use a polling loop to ensure Cognito is loaded before attaching the listener
  function attachCognitoListener() {
    if (typeof window !== 'undefined' && typeof (window as any).Cognito !== 'undefined' && typeof (window as any).Cognito.on === 'function') {
      try {
        (window as any).Cognito.on('afterSubmit', function(event: any) {
          console.log('[Attributely v2][Cognito] afterSubmit event received:', event);
          const handler = (window as any).providerHandlers && (window as any).providerHandlers['CognitoForm.AfterSubmit'];
          if (handler) {
            const normalized = handler(event);
            if (normalized && typeof window.AttributelyFormSubmit === 'function') {
              window.AttributelyFormSubmit(normalized.fields, normalized.formId);
            }
          }
        });
        console.log('[Attributely v2][Cognito] afterSubmit listener attached.');
      } catch (err) {
        console.error('[Attributely v2][Cognito] Error setting up afterSubmit listener:', err);
      }
    } else {
      // Try again in 200ms if Cognito is not ready
      setTimeout(attachCognitoListener, 200);
    }
  }
  attachCognitoListener();
})();

// --- Chat Widget Lead Tracking: Intercom, HubSpot Chat, Tawk.to, LiveChat, Tidio, Zendesk ---
(function () {
  // Helper to submit chat lead to Attributely
  function trackChatLead(provider: string, email: string, extraFields: Record<string, any> = {}) {
    if (!email) return;
    const fields = { email, chat_provider: provider, ...extraFields };
    if (typeof window.AttributelyFormSubmit === 'function') {
      window.AttributelyFormSubmit(fields, provider + '-chat');
    } else {
      // Buffer if AttributelyFormSubmit not ready
      window.__attrEarlyQueue = window.__attrEarlyQueue || [];
      window.__attrEarlyQueue.push({ fields, formId: provider + '-chat' });
    }
  }

  // --- Intercom ---
  function setupIntercom() {
    if (typeof window.Intercom === 'function') {
      // Listen for new messages (user reply)
      window.Intercom('onNewMessages', function (messages: any[]) {
        messages.forEach(function (msg: any) {
          if (msg.author && msg.author.email) {
            trackChatLead('Intercom', msg.author.email);
          }
        });
      });
    } else {
      setTimeout(setupIntercom, 500);
    }
  }
  setupIntercom();

  // --- HubSpot Chat ---
  function setupHubSpotChat() {
    if ((window as any).HubSpotConversations && typeof (window as any).HubSpotConversations.on === 'function') {
      (window as any).HubSpotConversations.on('conversationMessageSent', function (payload: any) {
        const email = payload?.message?.sender?.email;
        if (email) trackChatLead('HubSpotChat', email);
      });
    } else {
      setTimeout(setupHubSpotChat, 500);
    }
  }
  setupHubSpotChat();

  // --- Tawk.to ---
  function setupTawk() {
    if ((window as any).Tawk_API) {
      (window as any).Tawk_API.onPrechatSubmit = function (data: any) {
        if (data && data.email) {
          trackChatLead('Tawk.to', data.email);
        }
      };
    } else {
      setTimeout(setupTawk, 500);
    }
  }
  setupTawk();

  // --- LiveChat ---
  function setupLiveChat() {
    if ((window as any).LC_API && typeof (window as any).LC_API.on === 'function') {
      (window as any).LC_API.on('visitor_email', function (email: string) {
        if (email) trackChatLead('LiveChat', email);
      });
    } else {
      setTimeout(setupLiveChat, 500);
    }
  }
  setupLiveChat();

  // --- Tidio ---
  function setupTidio() {
    if ((window as any).tidioChatApi && typeof (window as any).tidioChatApi.on === 'function') {
      (window as any).tidioChatApi.on('emailSet', function (email: string) {
        if (email) trackChatLead('Tidio', email);
      });
    } else {
      setTimeout(setupTidio, 500);
    }
  }
  setupTidio();

  // --- Zendesk Chat ---
  function setupZendesk() {
    if ((window as any).zE && typeof (window as any).zE === 'function') {
      // Listen for user events (Zendesk's API is less direct)
      (window as any).zE('webWidget:on', 'userEvent', function (event: any) {
        if (event && event.email) {
          trackChatLead('Zendesk', event.email);
        }
      });
    } else {
      setTimeout(setupZendesk, 500);
    }
  }
  setupZendesk();

  // --- Fallback: MutationObserver for unknown chat widgets ---
  // Optionally, you can add a MutationObserver here to watch for email input in chat widgets if needed.
})();

// --- HubSpot Chat Integration ---
(function() {
  // Helper to funnel into your existing AttributelyFormSubmit
  function trackChatLead(provider: string, email: string, extraFields: Record<string, any> = {}) {
    if (!email) return;
    const fields = { email, chat_provider: provider, ...extraFields };
    if (typeof window.AttributelyFormSubmit === 'function') {
      window.AttributelyFormSubmit(fields, `${provider}-chat`);
    } else {
      window.__attrEarlyQueue = window.__attrEarlyQueue || [];
      window.__attrEarlyQueue.push({ fields, formId: `${provider}-chat` });
    }
  }

  function onConversationsAPIReady() {
    const hs = (window as any).HubSpotConversations;
    if (!hs || typeof hs.on !== 'function') return;

    // 1) visitor submits their email
    hs.on('contactAssociated', (payload: any) => {
      console.log('[Attributely] HubSpot contactAssociated →', payload);
      const email = payload?.contact?.email;
      if (email) trackChatLead('HubSpot', email);
    });

    // 2) visitor sends their first message
    hs.on('conversationStarted', (payload: any) => {
      console.log('[Attributely] HubSpot conversationStarted →', payload);
      // sometimes email is on payload.contact, sometimes on payload.message.sender
      const email = payload?.contact?.email || payload?.message?.sender?.email;
      const firstMsg = payload?.message?.text;
      trackChatLead('HubSpot', email, { firstMessage: firstMsg });
    });
  }

  // If the SDK's ready, run immediately; otherwise queue
  if ((window as any).HubSpotConversations) {
    onConversationsAPIReady();
  } else {
    (window as any).hsConversationsOnReady = (window as any).hsConversationsOnReady || [];
    (window as any).hsConversationsOnReady.push(onConversationsAPIReady);
  }
})();

export default AttributelyV2;